import { useState, useEffect, useCallback } from "react";
import { Button, Form } from "react-bootstrap";
import { listAppUsers, addAppStaff, setAppUserStatus, listAppNurseries, addAppNursery, removeAppNursery, setAppNurseryKind } from "../../services/fieldRatesService";
import { listVendors } from "../../services/catalogueService";
import { errorToast, successToast } from "../Toasts/Toasts";

// The MNB Rates Android app: who may sign in, and which nurseries it offers.
//
// Sign-in is a mobile number and a code on WhatsApp; only numbers added here
// get a code. Disabling takes effect on the person's next tap - the server
// re-reads the user on every request, whatever their token says.
//
// The nursery list is deliberately short: the vendors staff actually visit,
// not every supplier in Zoho. Anything else they type, and it reaches the
// vendor inbox under Unidentified.

const when = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "never");
const STATUS = { active: "bg-success", invited: "bg-secondary", disabled: "bg-danger" };

const AppUsers = () => {
  const [users, setUsers] = useState(null);
  const [nurseries, setNurseries] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setUsers(await listAppUsers());
      setNurseries(await listAppNurseries());
    } catch (e) {
      errorToast("Could not load app users");
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { listVendors().then(setVendors).catch(() => {}); }, []);

  const act = async (fn, done) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r && r.success === false) { errorToast(r.message); return false; }
      successToast(done);
      await load();
      return true;
    } catch (e) {
      errorToast(e?.response?.data?.message || e?.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim() || !/^\d{10}$/.test(phone.replace(/\D/g, "").slice(-10))) return errorToast("Enter a name and a 10-digit mobile");
    if (await act(() => addAppStaff({ name, phone }), `${name} can now sign in`)) { setName(""); setPhone(""); }
  };

  if (!users) return null;
  const listed = new Set(nurseries.map((n) => n.zohoVendorId));

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2">
          <h6 className="fw-bold mb-0">MNB Rates app</h6>
          <div className="small text-muted">Who can sign in to the Android app, and which nurseries it lists. Entries arrive in the Vendor Inbox.</div>
        </div>

        <div className="row g-3 px-2 mt-1">
          <div className="col-lg-7">
            <div className="card">
              <div className="card-header py-2"><strong className="small">Staff</strong></div>
              <div className="card-body">
                <form className="d-flex gap-2 mb-3 flex-wrap" onSubmit={add}>
                  <Form.Control size="sm" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 180 }} />
                  <Form.Control size="sm" placeholder="Mobile (WhatsApp)" value={phone} onChange={(e) => setPhone(e.target.value)} style={{ maxWidth: 180 }} />
                  <Button size="sm" type="submit" disabled={busy}>Add staff</Button>
                </form>
                <table className="table table-sm align-middle mb-0">
                  <thead><tr><th>Name</th><th>Mobile</th><th>Status</th><th>Last sign-in</th><th className="text-end">Entries</th><th></th></tr></thead>
                  <tbody>
                    {users.length === 0 && <tr><td colSpan={6} className="text-muted small">Add the first person above. They sign in with this number and a WhatsApp code.</td></tr>}
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.name}{u.role === "VENDOR" && <span className="badge bg-light text-dark ms-1">vendor</span>}</td>
                        <td className="small">{u.phone ? `+${u.phone.slice(0, 2)} ${u.phone.slice(2)}` : "—"}</td>
                        <td><span className={`badge ${STATUS[u.status] || "bg-secondary"}`}>{u.status}</span></td>
                        <td className="small text-muted">{when(u.lastLoginAt)}</td>
                        <td className="text-end">{u.entries}</td>
                        <td className="text-end">
                          {u.status === "disabled" ? (
                            <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => act(() => setAppUserStatus(u.id, "invited"), `${u.name} re-enabled`)}>Enable</Button>
                          ) : (
                            <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => act(() => setAppUserStatus(u.id, "disabled"), `${u.name} disabled`)}>Disable</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="card">
              <div className="card-header py-2"><strong className="small">Nurseries on the app</strong></div>
              <div className="card-body">
                <div className="d-flex gap-2 mb-3">
                  <Form.Select size="sm" value={pick} onChange={(e) => setPick(e.target.value)}>
                    <option value="">— add a vendor from Zoho —</option>
                    {vendors.filter((v) => !listed.has(v.id) && v.name !== "Unidentified").map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </Form.Select>
                  <Button size="sm" disabled={busy || !pick} onClick={async () => { if (await act(() => addAppNursery(pick), "Added to the app")) setPick(""); }}>Add</Button>
                </div>
                {nurseries.length === 0 && <div className="small text-muted">No nurseries yet. Staff can still type a name; it reaches the inbox as Unidentified.</div>}
                <ul className="list-group list-group-flush">
                  {nurseries.map((n) => (
                    <li key={n.id} className="list-group-item d-flex justify-content-between align-items-center px-0 py-1">
                      <span>
                        {n.name}
                        {!n.zohoVendorId && n.kind !== "competitor" && <span className="badge bg-light text-dark ms-1">not linked</span>}
                      </span>
                      <span className="d-flex gap-2 align-items-center">
                        <Form.Select size="sm" style={{ width: 130 }} value={n.kind || "supplier"} disabled={busy}
                          onChange={(e) => act(() => setAppNurseryKind(n.id, e.target.value), `${n.name}: ${e.target.value}`)}>
                          <option value="supplier">Supplier</option>
                          <option value="competitor">Competitor</option>
                        </Form.Select>
                        <Button size="sm" variant="link" className="text-danger p-0" disabled={busy} onClick={() => act(() => removeAppNursery(n.id), `${n.name} removed`)}>Remove</Button>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppUsers;
